<template>
  <div>
    <div
      v-if="firstTime"
      class="flex flex-col gap-4 text-left"
    >
      <p class="pt-2 text-left text-sm text-dark-gray">
        Enter contact information for the individual(s) to whom invoices should
        be sent.
      </p>
      <div>
        <label class="w-full text-left text-2xs font-bold uppercase">
          invoicing contact (Required)
        </label>
        <InvoicingContact
          :data-stored="reusableContacts"
          @update:fields="handleInvoicingContacts"
          @delete="handleDelete"
        />
      </div>
    </div>
    <div
      v-else
      class="flex flex-col gap-4 text-left"
    >
      <p class="pt-2 text-sm text-dark-gray">
        Confirm the contact(s) to whom invoices should be sent.
      </p>

      <section
        v-for="(contact, index) in stored"
        :key="`${index}-contact-identity-step-four`"
        class="mb-5"
      >
        <div class="mb-2">
          <label class="mb-1 text-xxs font-bold uppercase text-gray-over-dark">
            Name
          </label>
          <p class="mt-1 text-sm text-dark-gray">
            {{ contact.full_name }}
          </p>
        </div>
        <div class="flex gap-8">
          <div>
            <label
              class="mb-1 text-xxs font-bold uppercase text-gray-over-dark"
            >
              Phone
            </label>
            <p class="mt-1 text-sm text-dark-gray">
              {{ contact.phone }}
            </p>
          </div>
          <div>
            <label
              class="mb-1 text-xxs font-bold uppercase text-gray-over-dark"
            >
              Email
            </label>
            <p class="mt-1 text-sm text-dark-gray">
              {{ contact.email }}
            </p>
          </div>
        </div>
      </section>
    </div>
    <form-navigation-buttons
      v-if="!isNavigationDisabled"
      :enabled="!isNavigationDisabled"
      :component-identifier="componentIdentifier"
      :buttons-to-display="buttonsToDisplay"
      @next="handleNext"
      @back="handleBack"
      @edit="handleEdit"
    />
  </div>
</template>
<script setup>
import { useStore } from 'vuex';
import { ref, computed, watch } from 'vue';
import dayjs from 'dayjs';
import { useFormRouter } from '@/composables/formRouter';
import { isEmail, isPhone } from '@/common/validators.js';
import InvoicingContact from '@/components/InvoicingContact.vue';
import FormNavigationButtons from '@/components/FormNavigationButtons.vue';

const store = useStore();

const contacts = ref([]);
const componentIdentifier = 'Identity-4';

const invoicingContacts = computed(
  () =>
    store.state.carriers.carrierData.identity.billing_contacts
      .invoicing_contacts
);

const stored = computed(() => {
  return (
    Array.isArray(invoicingContacts.value) ? invoicingContacts.value : []
  ).map((contact) => {
    const { name, email_address, phone, ...rest } = contact;
    return {
      id: contact.id,
      full_name: name,
      email: email_address?.value,
      phone: phone?.value
        .replaceAll('(', '')
        .replaceAll(')', '')
        .replaceAll(' ', '-'),
      ...rest,
    };
  });
});

const reusableContacts = computed(() => {
  if (!stored.value.length) return [{ full_name: '', email: '', phone: '' }];
  return stored.value;
});

const isNavigationDisabled = computed(() => formRouter.isDisabled.value);
const activeStep = computed(
  () => store.state.formRoutes?.activeStep?.identifier
);
const buttonsToDisplay = computed(() =>
  firstTime.value ? 'continue-save' : 'continue-edit'
);
const firstTime = computed(
  () =>
    store.state.firstTime?.[formRouter.componentStep.value?.firstTimeIdentifier]
);
const onboardingPacketId = computed(
  () => store.state.onboardingPacket.onboarding_id
);
const carrierId = computed(
  () =>
    store.state.users.currentUser?.company?.carrier_id ||
    store.state.users.currentUser?.requested_carrier_id
);

const formRouter = useFormRouter(componentIdentifier);

const saveData = async () => {
  const formattedContacts = formatData(contacts.value);
  const newContacts = formattedContacts.filter((contact) => !contact.id);
  const updatedContacts = formattedContacts.filter((contact) => contact.id);

  const createOrUpdate = (method, contact) =>
    store.dispatch('contacts/createOrUpdateContact', {
      method,
      payload: contact,
    });

  const promises = [];

  if (newContacts.length > 0) {
    newContacts.forEach((contact) => {
      promises.push(createOrUpdate('post', contact));
    });
  }

  if (updatedContacts.length > 0) {
    updatedContacts.forEach((contact) => {
      promises.push(createOrUpdate('patch', contact));
    });
  }

  await Promise.all(promises);
};

const handleNext = async () => {
  store.commit(
    'addFormDebugMessage',
    componentIdentifier + ": 'handleNext' called"
  );
  if (isNavigationDisabled.value)
    return store.commit(
      'addFormDebugMessage',
      'ERROR: ' + componentIdentifier + ': formRouter is disabled, returning'
    );
  await saveData();
  formRouter.moveToNextStep();

  if (contacts.value.length) getOnboardingPacket();
};

const handleBack = () => {
  store.commit(
    'addFormDebugMessage',
    componentIdentifier + ": 'handleBack' called"
  );
  if (isNavigationDisabled.value)
    return store.commit(
      'addFormDebugMessage',
      'ERROR: ' + componentIdentifier + ': formRouter is disabled, returning'
    );
  formRouter.moveToPreviousStep();
};

const handleEdit = () => {
  store.commit(
    'addFormDebugMessage',
    componentIdentifier + ": 'handleEdit' called"
  );
  if (isNavigationDisabled.value)
    return console.log(
      'ERROR: ' + componentIdentifier + ': formRouter is disabled, returning'
    );
  formRouter.editActiveStep();
};

const handleInvoicingContacts = (updatedContacts) => {
  if (activeStep.value !== componentIdentifier) return;

  contacts.value = updatedContacts;
  const validEmails = updatedContacts.every(
    (value) => value.email && isEmail(value.email)
  );
  const validPhones = updatedContacts.every(
    (value) => value.phone && isPhone(value.phone)
  );
  const validNames = updatedContacts.every(
    (value) => value.full_name.length > 0
  );

  const isValid = validEmails && validPhones && validNames;

  if (isValid) {
    contacts.value = updatedContacts;
  }

  store.commit('toggleDisabledButton', {
    bool: !isValid,
    source: 'IdentityStepFour #2',
  });
};

const handleDelete = async (contactId) => {
  await store.dispatch('contacts/deleteContactById', contactId);
};

const emailAttributes = (value) => {
  return {
    is_type: 'invoicing',
    value: value,
  };
};

const phoneAttributes = (value) => {
  return {
    is_type: 'invoicing',
    value: value,
  };
};

const getOnboardingPacket = async () => {
  await store.dispatch('onboardingPacket/getOnboardingPacketById', {
    id: store.state.onboardingPacket.onboarding_id,
  });
};

const formatData = (contacts) => {
  const unchangedContacts = removeUnChangedContacts(contacts);

  return unchangedContacts.map((contact) => {
    const formattedContact = {
      carrier_id: carrierId.value,
      name: contact.full_name,
      is_type: 'invoicing',
      contactable_id: onboardingPacketId.value,
      contactable_type: 'OnboardingPacket',
      file_date: dayjs().format('YYYY-MM-DD'),
      email_address_attributes: emailAttributes(contact.email),
      phone_attributes: phoneAttributes(contact.phone),
    };

    if (contact.id) {
      formattedContact.id = contact.id;
    }

    return formattedContact;
  });
};

const removeUnChangedContacts = (contacts) => {
  return contacts.filter((contact) => {
    const storedContact = stored.value.find(
      (storedContact) => storedContact.id === contact.id
    );
    return (
      !storedContact ||
      storedContact.full_name !== contact.full_name ||
      storedContact.email !== contact.email ||
      storedContact.phone !== contact.phone
    );
  });
};

watch(activeStep, (newValue) => {
  if (newValue === componentIdentifier) {
    if (stored.value.length == 0) {
      store.commit('toggleDisabledButton', {
        bool: true,
        source: 'IdentityStepFour #1',
      });
      store.commit('firstTime/updateStepFirstTime', {
        step: 'Identity',
        value: true,
      });
    } else {
      store.commit('toggleDisabledButton', {
        bool: false,
        source: 'IdentityStepFour #3',
      });
      store.commit('firstTime/updateStepFirstTime', {
        step: 'Identity',
        value: false,
      });
    }
  }
});
</script>
